import type { ReactNode } from "react";
import { parseAnswer } from "./parseAnswer";

// Малкото markdown, което пише AI помощникът: **удебелено**, `код`, списъци
// („- ", „* ", „1. "), заглавия („# …") и абзаци. Текстът се сглобява в
// React елементи — никога не минава през dangerouslySetInnerHTML, защото в
// отговора има данни на живо (имена, текстове на грешки), не само базата.

const INLINE = /(\*\*[^*\n]+\*\*|`[^`\n]+`)/g;
/** Редовете, които правилата на помощника маркират като непроверени. */
const UNCONFIRMED_PREFIX = "Непотвърдено:";

/** **удебелено** и `код` в един ред; всичко друго остава текст. */
function renderInline(text: string, keyPrefix = "i"): ReactNode[] {
  const nodes: ReactNode[] = [];
  let rest = text;
  if (rest.startsWith(UNCONFIRMED_PREFIX)) {
    nodes.push(
      <span key={`${keyPrefix}-unconfirmed`} className="font-semibold text-brand-deep-ink">
        {UNCONFIRMED_PREFIX}
      </span>,
    );
    rest = rest.slice(UNCONFIRMED_PREFIX.length);
  }
  rest.split(INLINE).forEach((part, index) => {
    if (!part) return;
    const key = `${keyPrefix}-${index}`;
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) {
      nodes.push(
        <strong key={key} className="font-semibold text-ink">
          {part.slice(2, -2)}
        </strong>,
      );
    } else if (part.startsWith("`") && part.endsWith("`") && part.length > 2) {
      nodes.push(
        <code
          key={key}
          className="rounded-[4px] bg-fill px-[5px] py-[1px] font-pos-mono text-[0.88em] text-ink"
        >
          {part.slice(1, -1)}
        </code>,
      );
    } else {
      nodes.push(part);
    }
  });
  return nodes;
}

const DEPTH_INDENT = ["pl-0", "pl-[18px]", "pl-[36px]", "pl-[54px]"];

export const AssistantMarkdown = ({ text }: { text: string }) => (
  // Текстова мярка (~80 знака на ред), не кап на страницата: на широк монитор
  // абзац през целия екран не се чете.
  <div className="flex max-w-[82ch] flex-col gap-[10px] text-[14px] leading-[1.6] text-body">
    {parseAnswer(text).map((block, blockIndex) => {
      if (block.kind === "heading") {
        return (
          <div key={blockIndex} className="text-[14.5px] font-bold leading-[1.3] text-ink">
            {renderInline(block.text, `h${blockIndex}`)}
          </div>
        );
      }
      if (block.kind === "paragraph") {
        return (
          <p key={blockIndex} className="m-0">
            {block.lines.map((line, lineIndex) => (
              <span key={lineIndex}>
                {lineIndex > 0 && <br />}
                {renderInline(line, `p${blockIndex}-${lineIndex}`)}
              </span>
            ))}
          </p>
        );
      }
      const ListTag = block.ordered ? "ol" : "ul";
      return (
        <ListTag
          key={blockIndex}
          className={`m-0 flex flex-col gap-[4px] pl-[20px] ${block.ordered ? "list-decimal" : "list-disc"}`}
        >
          {block.items.map((item, itemIndex) => (
            <li key={itemIndex} className={DEPTH_INDENT[item.depth]}>
              {renderInline(item.text, `l${blockIndex}-${itemIndex}`)}
            </li>
          ))}
        </ListTag>
      );
    })}
  </div>
);
